import { isTruthy, WILDCARD } from '@ardoq/common-helpers';
import { RawGraphEdge2, RawGraphNode2, URLFieldValue } from '../types';
import { DIFF_TYPE } from '@ardoq/global-consts';
import { CollapsedGraphReference, HasGetDistinctReferenceKey } from './types';
import { DiffType } from '@ardoq/data-model';
import { getOtherLabelsForReference } from '../labels/getOtherLabelsForReference';
import { legacyGetDistinctReferenceKey as defaultGetDistinctReferenceKey } from './getDistinctReferenceKey';
import { GraphInterface } from '../graphInterface/graphInterface';
import { RepresentationInterface } from '../representationInterface/representationInterface';
import { LabelsInterface } from '../labelsInterface/labelsInterface';
import { type I18nLocale } from '@ardoq/i18n';

interface HasCollapsedAncestorArgs {
  groupMap: Map<string, RawGraphNode2>;
  collapsedGroupIds: Set<string>;
  item: RawGraphNode2;
}
const hasCollapsedAncestor = ({
  item,
  groupMap,
  collapsedGroupIds,
}: HasCollapsedAncestorArgs): boolean => {
  if (!item.parentId) {
    return false;
  }
  const parentGroup = groupMap.get(item.parentId);
  if (!parentGroup) {
    return false;
  }
  return (
    collapsedGroupIds.has(WILDCARD) ||
    collapsedGroupIds.has(parentGroup.id) ||
    hasCollapsedAncestor({ item: parentGroup, groupMap, collapsedGroupIds })
  );
};
interface AggregateReferencesArgsBase {
  componentMap: Map<string, RawGraphNode2>;
  groupMap: Map<string, RawGraphNode2>;
  collapsedGroupIds: Set<string>;
}
interface AggregationCandidatesReducerState extends AggregateReferencesArgsBase {
  aggregatedReference: RawGraphEdge2 | null;
  aggregationRejects: RawGraphEdge2[];
}

const mergeModelIds = (refA: RawGraphEdge2, refB: CollapsedGraphReference) => {
  if (refA.modelIds && refB.modelIds)
    return refA.modelIds.concat(refB.modelIds);

  if (refA.modelIds && refB.modelId) {
    refA.modelIds.push(refB.modelId);
    return refA.modelIds;
  }

  if (refA.modelId && refB.modelIds) {
    refB.modelIds.push(refA.modelId);
    return refB.modelIds;
  }

  if (refA.modelId && refB.modelId) return [refA.modelId, refB.modelId];

  return [];
};

function mergeAggregatedReference(
  aggregatedReference: RawGraphEdge2,
  current: CollapsedGraphReference
) {
  // note: ugly implementation because it's on a hot path and we can't allocate too much
  aggregatedReference.id = `${aggregatedReference.id},${current.id}`;
  aggregatedReference.modelIds = mergeModelIds(aggregatedReference, current);
  aggregatedReference.modelId = undefined;
  return aggregatedReference;
}

const processAggregationCandidates = (
  state: AggregationCandidatesReducerState,
  current: CollapsedGraphReference
) => {
  /** true if either the source or target node has a collapsed ancestor. */
  const endpointItems = [
    current.uncollapsedSourceId,
    current.uncollapsedTargetId,
  ].map(
    itemId =>
      (itemId &&
        (state.componentMap.get(itemId) || state.groupMap.get(itemId))) ||
      null
  );
  const isAggregationCandidate =
    endpointItems
      .filter(isTruthy)
      .map(item =>
        hasCollapsedAncestor({
          item,
          groupMap: state.groupMap,
          collapsedGroupIds: state.collapsedGroupIds,
        })
      )
      .filter(isTruthy).length > 0;

  if (isAggregationCandidate) {
    state.aggregatedReference = state.aggregatedReference
      ? mergeAggregatedReference(state.aggregatedReference, current)
      : current;
  } else {
    state.aggregationRejects.push(current);
  }
  return state;
};
interface AggregationCandidatesListReducerState extends AggregateReferencesArgsBase {
  referenceList: RawGraphEdge2[];
}
const processAggregationCandidatesList = (
  state: AggregationCandidatesListReducerState,
  current: CollapsedGraphReference[]
) => {
  const { componentMap, groupMap, collapsedGroupIds } = state;
  const { aggregationRejects, aggregatedReference } = current.reduce(
    processAggregationCandidates,
    {
      componentMap,
      groupMap,
      collapsedGroupIds,
      aggregatedReference: null,
      aggregationRejects: [],
    }
  );
  state.referenceList.push(...aggregationRejects);
  if (aggregatedReference) {
    state.referenceList.push(aggregatedReference);
  }
  return state;
};
interface AggregateReferencesArgs
  extends HasGetDistinctReferenceKey, AggregateReferencesArgsBase {
  viewInterface: GraphInterface & RepresentationInterface & LabelsInterface;
  referenceList: CollapsedGraphReference[];
  isInScopeDiffMode: boolean;
  urlFieldValuesByReferenceId: Map<string, URLFieldValue[] | null>;
  i18nLocale: I18nLocale;
  /** Override the label used for aggregation key. Defaults to getLegacyReferenceDisplayLabel. */
  getLabel?: (reference: CollapsedGraphReference) => string;
}
export const aggregateReferences = ({
  viewInterface,
  referenceList,
  getDistinctReferenceKey = defaultGetDistinctReferenceKey,
  componentMap,
  groupMap,
  collapsedGroupIds,
  isInScopeDiffMode,
  urlFieldValuesByReferenceId,
  i18nLocale,
  getLabel,
}: AggregateReferencesArgs) => {
  const distinctReferenceMap = new Map<string, CollapsedGraphReference[]>();

  referenceList
    // spread to create a shallow copy as the optimized implementation will modify them
    .map(reference => ({ ...reference }))
    .forEach(reference => {
      const modelId = reference.modelId ?? 'unknown'; // 'unknown' should never happen and would indicate an error elsewhere
      const diffState = isInScopeDiffMode
        ? viewInterface.getReferenceAttribute(modelId, DIFF_TYPE)
        : DiffType.NONE;
      const lineColor = viewInterface.getReferenceCssColors(modelId, {
        useAsBackgroundStyle: false,
      })?.stroke;
      const otherLabels = getOtherLabelsForReference(
        viewInterface,
        modelId,
        i18nLocale
      );
      const label = getLabel
        ? getLabel(reference)
        : (viewInterface.getLegacyReferenceDisplayLabel(reference.id) ?? '');
      const distinctKey = getDistinctReferenceKey({
        globalTypeId: viewInterface.getGlobalReferenceTypeId(modelId) ?? '',
        label,
        sourceGraphNodeId: reference.sourceId,
        targetGraphNodeId: reference.targetId,
        isParentChildReference: reference.isParentChildReference,
        diffState,
        otherLabels,
        lineColor,
        urlFieldValues: urlFieldValuesByReferenceId.get(modelId),
      });
      if (distinctReferenceMap.has(distinctKey)) {
        distinctReferenceMap.get(distinctKey)!.push(reference);
      } else {
        distinctReferenceMap.set(distinctKey, [reference]);
      }
    });

  return Array.from(distinctReferenceMap.values()).reduce(
    processAggregationCandidatesList,
    { componentMap, groupMap, collapsedGroupIds, referenceList: [] }
  ).referenceList;
};
